import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getAgentById } from '../../../../services/agents';

/**
 * AgentContext - Contexto para datos del agente
 * 
 * Contiene:
 * - Datos del agente (name, status, uuid, starters, model, etc.)
 * - Funciones setter para edición directa
 * - Función para fetch/refresh
 * 
 * Las páginas editan directamente en el contexto.
 * El autosave se maneja en cada página que lo necesite.
 */

const AgentContext = createContext(null);

export const AgentProvider = ({ children }) => {
  const { agentId } = useParams();

  // Estado del agente
  const [agent, setAgent] = useState({
    uuid: null,
    name: '',
    status: 'draft',
    starters: [],
    model: 'gpt-4',
    instructions: '',
    photoUrl: '',
    type: 'assistant',
    category: '',
    description: '',
    vaultUuid: null, // UUID del vault propio del agente
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch agent data
  const fetchAgent = useCallback(async (uuid) => {
    if (!uuid) return;

    setLoading(true);
    setError(null);

    try {
      const response = await getAgentById(uuid);

      if (response?.data) {
        const agentData = response.data;
        
        // Prioridad: starters > conversation_starters > metadata.conversation_starters
        // Verificamos que el array tenga elementos, no solo que exista
        let starters = [];
        if (Array.isArray(agentData.starters) && agentData.starters.length > 0) {
          starters = agentData.starters;
        } else if (Array.isArray(agentData.conversation_starters) && agentData.conversation_starters.length > 0) {
          starters = agentData.conversation_starters;
        } else if (Array.isArray(agentData.metadata?.conversation_starters) && agentData.metadata.conversation_starters.length > 0) {
          starters = agentData.metadata.conversation_starters;
        }
        
        let vaultUuid = agentData.vaultUuid || null;
        
        if (!vaultUuid) {
          const MAX_RETRY_TIME = 30000;
          const RETRY_INTERVAL = 1500;
          const startTime = Date.now();
          
          while (!vaultUuid && (Date.now() - startTime) < MAX_RETRY_TIME) {
            try {
              console.log('AgentContext: vaultUuid is null, retrying getAgentById...');
              const retryResponse = await getAgentById(uuid);
              if (retryResponse?.data?.vaultUuid) {
                vaultUuid = retryResponse.data.vaultUuid;
                console.log('AgentContext: vaultUuid obtained on retry:', vaultUuid);
                break;
              }
            } catch (retryError) {
              console.warn('AgentContext: Retry failed:', retryError);
            }
            
            if (!vaultUuid && (Date.now() - startTime) < MAX_RETRY_TIME) {
              await new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL));
            }
          }
          
          if (!vaultUuid) {
            console.warn('AgentContext: vaultUuid not obtained after 10 seconds of retries');
          }
        }
        
        setAgent({
          uuid: agentData.uuid || uuid,
          name: agentData.name || '',
          status: agentData.status || 'draft',
          starters,
          model: agentData.model || 'gpt-4',
          instructions: agentData.instructions || agentData.humanInstructions || '',
          photoUrl: agentData.photoUrl || agentData.image || '',
          type: agentData.type || 'assistant',
          category: agentData.category || '',
          description: agentData.description || '',
          vaultUuid: vaultUuid, // UUID del vault propio del agente
        });
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      console.error('Error fetching agent:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar agente cuando cambia el ID
  useEffect(() => {
    if (agentId) {
      fetchAgent(agentId);
    }
  }, [agentId, fetchAgent]);

  // Refrescar datos del agente
  const refreshAgent = useCallback(() => {
    if (agentId) {
      fetchAgent(agentId);
    }
  }, [agentId, fetchAgent]);

  // Actualizar datos del agente (múltiples campos a la vez)
  const updateAgentData = useCallback((updates) => {
    setAgent((prev) => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  }, []);

  // ============ SETTERS INDIVIDUALES ============

  const setAgentName = useCallback((name) => {
    setAgent((prev) => ({ ...prev, name }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentDescription = useCallback((description) => {
    setAgent((prev) => ({ ...prev, description }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentModel = useCallback((model) => {
    setAgent((prev) => ({ ...prev, model }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentInstructions = useCallback((instructions) => {
    setAgent((prev) => ({ ...prev, instructions }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentPhotoUrl = useCallback((photoUrl) => {
    setAgent((prev) => ({ ...prev, photoUrl }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentType = useCallback((type) => {
    setAgent((prev) => ({ ...prev, type }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentCategory = useCallback((category) => {
    setAgent((prev) => ({ ...prev, category }));
    setHasUnsavedChanges(true);
  }, []);

  const setAgentStatus = useCallback((status) => {
    setAgent((prev) => ({ ...prev, status }));
    setHasUnsavedChanges(true);
  }, []);

  // ============ STARTERS ============

  const addStarter = useCallback((starter) => {
    setAgent((prev) => ({
      ...prev,
      starters: [...prev.starters, starter],
    }));
    setHasUnsavedChanges(true);
  }, []);

  const removeStarter = useCallback((index) => {
    setAgent((prev) => ({
      ...prev,
      starters: prev.starters.filter((_, i) => i !== index),
    }));
    setHasUnsavedChanges(true);
  }, []);

  const setStarters = useCallback((starters) => {
    setAgent((prev) => ({ ...prev, starters }));
    setHasUnsavedChanges(true);
  }, []);

  // Marcar como guardado
  const markAsSaved = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const value = useMemo(() => ({
    // Datos
    agent,
    agentId,
    loading,
    error,
    hasUnsavedChanges,

    // Acciones generales
    fetchAgent,
    refreshAgent,
    updateAgentData,
    markAsSaved,

    // Setters individuales
    setAgentName,
    setAgentDescription,
    setAgentModel,
    setAgentInstructions,
    setAgentPhotoUrl,
    setAgentType,
    setAgentCategory,
    setAgentStatus,

    // Starters
    addStarter,
    removeStarter,
    setStarters,
  }), [
    agent,
    agentId,
    loading,
    error,
    hasUnsavedChanges,
    fetchAgent,
    refreshAgent,
    updateAgentData,
    markAsSaved,
    setAgentName,
    setAgentDescription,
    setAgentModel,
    setAgentInstructions,
    setAgentPhotoUrl,
    setAgentType,
    setAgentCategory,
    setAgentStatus,
    addStarter,
    removeStarter,
    setStarters,
  ]);

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

// Hook para usar el contexto
export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within AgentProvider');
  }
  return context;
};

export default AgentContext;
